/**
 * Asset schemas for AI Reports.
 *
 * **Dual purpose:** These Zod schemas serve two roles:
 * 1. Runtime validation and type inference for TypeScript
 * 2. JSON Schema generation for LLM tool calls (the `.describe()` strings are written as prompts for the AI)
 *
 * Asset metadata and structure are generated by the AI Reports backend and consumed by the frontend.
 */

// typescript
import {z} from 'zod';
import {EdgeSchema, FamilyNodeSchema} from './visjs-schema';

/**
 * Shared width/height shape for visual (renderable) asset types.
 */
export const zDimensionsSchema = z.object({
	width: z.number().int().nonnegative(),
	height: z.number().int().nonnegative(),
});
export type Dimensions = z.infer<typeof zDimensionsSchema>;


/**
 * Metadata associated with an asset.
 */
export const AssetMetadataSchema = z.object({
	id: z.string().min(1),
	displayName: z.string(),
	mimeType: z.string().nullable(),
	description: z.string(),
	createdAt: z.iso.datetime().default(() => new Date().toISOString()),
	modifiedAt: z.iso.datetime().default(() => new Date().toISOString()),
});
export type AssetMetadata = z.infer<typeof AssetMetadataSchema>;

export const zStoragePathSchema = z.object({
	storagePath: z.string(),
});

/**
 * Likeness classification signal (EU AI Act Art. 50(4), FT-955).
 *
 * Set by the DeepAgent image tool at tool-call time to mark an asset as a likeness
 * reconstruction of a specific real, identifiable person (e.g. an avatar/fine-tune-style
 * portrait of a named ancestor). Decorative/generic AI-generated content (backgrounds,
 * illustrations, ornaments) leaves this false/absent. Read later by the image finalization
 * choke point (FT-953) to decide whether a visible AI-disclosure badge is required.
 */
export const zLikenessSchema = z.object({
	isLikeness: z
		.boolean()
		.default(false)
		.describe(
			'True if this image is a likeness reconstruction of a specific real, identifiable person (e.g. an avatar or portrait of a named ancestor). False for decorative, generic, or non-identifiable imagery (backgrounds, illustrations, ornaments).'
		),
});

/**
 * Records which EU AI Act Art. 50(2)/(4) disclosure marking was actually applied to an
 * asset's bytes (FT-958), for compliance audit purposes — not just whether marking was
 * attempted. Set by the marking steps (ImageService/VideoService `markAsAiGenerated`)
 * once bytes are produced; omitted if marking never ran or failed entirely.
 */
export const zDisclosureAppliedSchema = z.object({
	disclosureApplied: z
		.enum(['metadata', 'metadata+badge'])
		.optional()
		.describe(
			'Which AI-disclosure marking was embedded in this asset\'s bytes: invisible metadata only, or metadata plus a visible corner badge.'
		),
});

/**
 * Base64 asset (no metadata merged here).
 */
export const AssetBase64Schema = z
	.object({
		type: z.literal('base64'),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape)
	.extend(zDimensionsSchema.shape)
	.extend(zLikenessSchema.shape)
	.extend(zDisclosureAppliedSchema.shape);
export type AssetBase64 = z.infer<typeof AssetBase64Schema>;

/**
 * URL asset.
 */
export const AssetUrlSchema = z
	.object({
		type: z.literal('url'),
		url: z.string(),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zDimensionsSchema.partial().shape)
	.extend(zLikenessSchema.shape);
export type AssetUrl = z.infer<typeof AssetUrlSchema>;

/**
 * SVG source asset.
 * Contains the SVG defs and body references to the assets.
 */
export const AssetSvgSourceSchema = z
	.object({
		type: z.literal('svg-source'),
		defIds: z.array(
			z
				.string()
				.describe(
					`Ids of SVG assets to be placed inside <defs>. They can be referenced inside the body using the syntax #id, e.g. <use xlink:href="#my-asset"/>. Don't includer the def section itself, just the list ids of assets to be used inside the defs.`
				)
		),
		skipProgrammaticLines: z.boolean().optional(),
	})
	.extend(zDimensionsSchema.shape)
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape);
export type AssetSvgSource = z.infer<typeof AssetSvgSourceSchema>;

/**
 * SVG defs asset.
 */
export const AssetSvgDefsSchema = z
	.object({
		type: z.literal('svg-defs'),
		exampleBody: z
			.string()
			// .optional()
			.describe(
				`One or more SVG elements that demonstrate how to use the def. Do not include the outer <svg> tag.
<svg>
<defs>
<!--assume defs are here -->
</defs>
<!--exampleBody goes here-->
</svg>`
			),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape);
export type AssetSvgDefs = z.infer<typeof AssetSvgDefsSchema>;

/**
 * Vis.js asset.
 */
export const zVisjsJsonSchema = z.object({
	nodes: z.array(FamilyNodeSchema),
	edges: z.array(EdgeSchema).default([]),
	backgroundImage: z.string().nullable(),
});

export type VisjsJson = z.infer<typeof zVisjsJsonSchema>;

export const AssetVisjsSchema = z
	.object({ type: z.literal('visjs') })
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape)
	.extend(zDimensionsSchema.shape);
export type AssetVisjs = z.infer<typeof AssetVisjsSchema>;

/**
 * Family tree resource.
 */

export const AssetFamilyTreeSchema = z
	.object({
		type: z.literal('family-tree'),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape);
export type AssetFamilyTree = z.infer<typeof AssetFamilyTreeSchema>;

/**
 * A single life event for a person, with references to photo asset IDs.
 * Full event list is stored as JSON in Supabase Storage (not inline on the asset).
 */
export const PersonEventSchema = z.object({
	eventId: z.number().nullable(),
	eventName: z.string().nullable(),
	date: z.string().nullable(),
	place: z.string().nullable(),
	description: z.string().nullable(),
	photoAssetIds: z.array(z.string()),
});
export type PersonEvent = z.infer<typeof PersonEventSchema>;

/**
 * Person events asset — metadata + storagePath only; full JSON in Supabase Storage.
 */
export const AssetPersonEventsSchema = z
	.object({
		type: z.literal('person-events'),
		personId: z.number(),
		personName: z.string(),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape);
export type AssetPersonEvents = z.infer<typeof AssetPersonEventsSchema>;

/**
 * Layout position node — stored inside AssetLayout JSON.
 */
export const zLayoutNodePosition = z.object({
	id: z.string(),
	x: z.number(),
	y: z.number(),
	width: z.number(),
	height: z.number(),
	label: z.string().optional(),
});
export type LayoutNodePosition = z.infer<typeof zLayoutNodePosition>;

/**
 * JSON shape stored in Supabase for an AssetLayout.
 * Contains raw node positions only — promptBlock and connectionLinesSvg are generated on-demand.
 */
export const zLayoutAssetJson = z.object({
	nodes: z.array(zLayoutNodePosition),
	edges: z.array(z.object({ from: z.string(), to: z.string() })),
	width: z.number(),
	height: z.number(),
});
export type LayoutAssetJson = z.infer<typeof zLayoutAssetJson>;

/**
 * Layout asset — stores pre-computed node positions for a family tree.
 */
export const AssetLayoutSchema = z
	.object({
		type: z.literal('layout'),
		width: z.number(),
		height: z.number(),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape);
export type AssetLayout = z.infer<typeof AssetLayoutSchema>;

/**
 * Cytoscape.js graph asset.
 */
export const AssetCytoscapeSchema = z
	.object({ type: z.literal('cytoscape') })
	.extend(AssetMetadataSchema.shape)
	.extend(zStoragePathSchema.shape)
	.extend(zDimensionsSchema.shape);
export type AssetCytoscape = z.infer<typeof AssetCytoscapeSchema>;

/**
 * AssetVideo — async-generated video clip via a video provider (e.g. Seedance via fal.ai).
 * Created with status='pending' by the tool; updated to 'ready' or 'failed' by the webhook.
 */
export const AssetVideoSchema = z
	.object({
		type: z.literal('video'),
		status: z.enum(['pending', 'ready', 'failed']),
		jobId: z.string().describe('Provider request ID (fal.ai request_id). Debug/cancel only.'),
		provider: z.enum(['seedance-falai']),
		storagePath: z.string().nullable(),
		thumbnailStoragePath: z.string().nullable(),
		durationSec: z.number().int().positive(),
		width: z.number().int().positive(),
		height: z.number().int().positive(),
		refAssetIds: z.array(z.string()),
		prompt: z.string(),
		errorMessage: z.string().optional(),
		altText: z
			.string()
			.optional()
			.describe(
				'Accessible-text equivalent of the visible AI-disclosure badge (EU AI Act Art. 50(5)), set only when the badge is applied (isLikeness videos). Frontend should surface this as an aria-label on the video element.'
			),
	})
	.extend(AssetMetadataSchema.shape)
	.extend(zLikenessSchema.shape)
	.extend(zDisclosureAppliedSchema.shape);
export type AssetVideo = z.infer<typeof AssetVideoSchema>;

/**
 * Full Asset: discriminator union, each merged with metadata.
 */
export const zAsset = z.discriminatedUnion('type', [
	AssetBase64Schema,
	AssetUrlSchema,
	AssetSvgDefsSchema,
	AssetSvgSourceSchema,
	AssetVisjsSchema,
	AssetFamilyTreeSchema,
	AssetPersonEventsSchema,
	AssetLayoutSchema,
	AssetCytoscapeSchema,
	AssetVideoSchema,
]);
export type Asset = z.infer<typeof zAsset>;

/** Response shape for POST /api/video/check/:projectId/:threadId/:assetId */
export type VideoCheckResponse = {
	status: AssetVideo['status'];
	asset?: AssetVideo;
};
